import { useTranslation } from 'react-i18next';
import { BarChart3, Lightbulb, Zap, type LucideIcon } from 'lucide-react';
import { Card, CardDescription, CardHeader, CardTitle } from '@bliks-io/ui';
import { PartnerPageHeader } from '@/pages/partner/components/PartnerPageHeader';
import { PartnerSalesTabsNav } from '@/pages/partner/components/PartnerSalesTabsNav';

const BENEFITS: { key: string; icon: LucideIcon }[] = [
  { key: 'value', icon: Zap },
  { key: 'numbers', icon: BarChart3 },
  { key: 'expertise', icon: Lightbulb }
];

const REFERRAL_STEPS = ['access', 'convert', 'earn', 'renew'] as const;

const CONTRACT_YEARS = ['year1', 'year2', 'year3'] as const;

export function PartnerProgramPage() {
  const { t } = useTranslation();

  return (
    <div className="flex h-full flex-col">
      <PartnerSalesTabsNav />
      <div className="flex min-h-0 flex-1 flex-col gap-10 overflow-auto p-6">
        <PartnerPageHeader
          title={t('partner.program.title')}
          subtitle={t('partner.program.subtitle')}
        />

        <section className="flex max-w-5xl flex-col gap-4">
          <SectionIntro
            eyebrow={t('partner.program.benefitsEyebrow')}
            title={t('partner.program.benefitsTitle')}
            intro={t('partner.program.benefitsIntro')}
          />
          <div className="grid gap-4 md:grid-cols-3">
            {BENEFITS.map(({ key, icon: Icon }) => (
              <Card key={key}>
                <CardHeader>
                  <Icon className="h-5 w-5 text-primary" />
                  <CardTitle>{t(`partner.program.benefits.${key}.title`)}</CardTitle>
                  <CardDescription>{t(`partner.program.benefits.${key}.text`)}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </section>

        <section className="flex max-w-5xl flex-col gap-4">
          <SectionIntro
            eyebrow={t('partner.program.referralEyebrow')}
            title={t('partner.program.referralTitle')}
            intro={t('partner.program.referralIntro')}
          />
          <ol className="flex flex-col gap-3">
            {REFERRAL_STEPS.map((step, index) => (
              <li key={step} className="flex gap-4 rounded-lg border bg-muted p-4">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary text-sm font-semibold text-primary-foreground">
                  {index + 1}
                </span>
                <div>
                  <p className="font-medium">{t(`partner.program.steps.${step}.title`)}</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {t(`partner.program.steps.${step}.text`)}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className="flex max-w-5xl flex-col gap-4">
          <h2 className="text-xl font-semibold">{t('partner.program.commissionTitle')}</h2>
          <div className="grid gap-3 md:grid-cols-3">
            {CONTRACT_YEARS.map((year, index) => (
              <div key={year} className="flex flex-col gap-2">
                <p
                  className={
                    index === 0
                      ? 'rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground'
                      : 'rounded-md bg-secondary px-3 py-2 text-sm font-medium text-secondary-foreground'
                  }
                >
                  {t(`partner.program.commission.${year}.label`)}
                </p>
                <p className="text-sm">{t(`partner.program.commission.${year}.text`)}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">{t('partner.program.commissionFootnote')}</p>
        </section>
      </div>
    </div>
  );
}

function SectionIntro({
  eyebrow,
  title,
  intro
}: {
  eyebrow: string;
  title: string;
  intro: string;
}) {
  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {eyebrow}
      </p>
      <h2 className="text-xl font-semibold">{title}</h2>
      <p className="max-w-3xl text-sm text-muted-foreground">{intro}</p>
    </div>
  );
}
